// File: src/AdminPanel.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  Typography,
  CssBaseline,
  Toolbar,
  AppBar,
  Button
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CategoryIcon from '@mui/icons-material/Category';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ScaleIcon from '@mui/icons-material/Scale';
import LogoutIcon from '@mui/icons-material/Logout';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import NotesIcon from '@mui/icons-material/Notes';
import BuildIcon from '@mui/icons-material/Build';

// Managers
import BeverageRecipeManager from './BeverageRecipeManager';
import DessertRecipeManager from './DessertRecipeManager';
import CategoryManager from './CategoryManager';
import SizeManager from './SizeManager';
import IngredientManager from './IngredientManager';
import VendorManager from './VendorManager';
import UnitManager from './UnitManager';
import FlavorManager from './FlavorManager';
import ExportManager from './ExportManager';
import RecipeNotesManager from './RecipeNotesManager';
import EquipmentManager from './EquipmentManager';
import ProcessRecipeManager from './ProcessRecipeManager';
import ContainersManager from './ContainersManager';

import Login from './Login';
import { auth } from './firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';

const drawerWidth = 240;

const AdminPanel = () => {
  const [page, setPage] = useState('beverages');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => setUser(currentUser));
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    signOut(auth);
    setUser(null);
  };

  const renderPage = () => {
    switch (page) {
      case 'beverages':
        return <BeverageRecipeManager />;

      // Process recipes (mounted with distinct keys to force clean state per tab)
      case 'cooking':
        return <ProcessRecipeManager key="cooking" defaultType="cooking" lockType />;
      case 'mixing':
        return <ProcessRecipeManager key="mixing" defaultType="mixing" lockType />;
      case 'prep':
        return <ProcessRecipeManager key="prep" defaultType="prep" lockType />;

      case 'desserts':
        return <DessertRecipeManager />;
      case 'notes':
        return <RecipeNotesManager />;

      case 'categories':
        return <CategoryManager />;
      case 'sizes':
        return <SizeManager />;
      case 'flavors':
        return <FlavorManager />;

      case 'ingredients':
        return <IngredientManager />;
      case 'vendors':
        return <VendorManager />;
      case 'equipment':
        return <EquipmentManager />;
      case 'containers':
        return <ContainersManager />;
      case 'units':
        return <UnitManager />;

      case 'export':
        return <ExportManager />;

      default:
        return null;
    }
  };

  if (!user) return <Login onLogin={setUser} />;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: 1201 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap>Panda Recipes Manager</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body1">{user?.displayName}</Typography>
            <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>Logout</Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' }
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          {/* Recipes */}
          <List subheader={<ListSubheader>Recipes</ListSubheader>}>
            <ListItemButton selected={page==='beverages'} onClick={()=>setPage('beverages')}>
              <ListItemIcon><RestaurantMenuIcon /></ListItemIcon>
              <ListItemText primary="Beverages" />
            </ListItemButton>
            <ListItemButton selected={page==='desserts'} onClick={()=>setPage('desserts')}>
              <ListItemIcon><RestaurantMenuIcon /></ListItemIcon>
              <ListItemText primary="Desserts" />
            </ListItemButton>
            <ListItemButton selected={page==='notes'} onClick={()=>setPage('notes')}>
              <ListItemIcon><NotesIcon /></ListItemIcon>
              <ListItemText primary="Recipe Notes" />
            </ListItemButton>
          </List>

          {/* Process Recipes */}
          <List subheader={<ListSubheader>Process Recipes</ListSubheader>}>
            <ListItemButton selected={page==='cooking'} onClick={()=>setPage('cooking')}>
              <ListItemIcon><BuildIcon /></ListItemIcon>
              <ListItemText primary="Cooking Recipes" />
            </ListItemButton>
            <ListItemButton selected={page==='mixing'} onClick={()=>setPage('mixing')}>
              <ListItemIcon><BuildIcon /></ListItemIcon>
              <ListItemText primary="Mixing Recipes" />
            </ListItemButton>
            <ListItemButton selected={page==='prep'} onClick={()=>setPage('prep')}>
              <ListItemIcon><BuildIcon /></ListItemIcon>
              <ListItemText primary="Prep / Cutting Recipes" />
            </ListItemButton>
          </List>

          {/* Management */}
          <List subheader={<ListSubheader>Management</ListSubheader>}>
            <ListItemButton selected={page==='categories'} onClick={()=>setPage('categories')}>
              <ListItemIcon><CategoryIcon /></ListItemIcon>
              <ListItemText primary="Categories" />
            </ListItemButton>
            <ListItemButton selected={page==='sizes'} onClick={()=>setPage('sizes')}>
              <ListItemIcon><FormatSizeIcon /></ListItemIcon>
              <ListItemText primary="Sizes" />
            </ListItemButton>
            <ListItemButton selected={page==='flavors'} onClick={()=>setPage('flavors')}>
              <ListItemIcon><CategoryIcon /></ListItemIcon>
              <ListItemText primary="Flavors" />
            </ListItemButton>
          </List>

          {/* Inventory */}
          <List subheader={<ListSubheader>Inventory</ListSubheader>}>
            <ListItemButton selected={page==='ingredients'} onClick={()=>setPage('ingredients')}>
              <ListItemIcon><Inventory2Icon /></ListItemIcon>
              <ListItemText primary="Ingredients" />
            </ListItemButton>
            <ListItemButton selected={page==='vendors'} onClick={()=>setPage('vendors')}>
              <ListItemIcon><Inventory2Icon /></ListItemIcon>
              <ListItemText primary="Vendors" />
            </ListItemButton>
            <ListItemButton selected={page==='equipment'} onClick={()=>setPage('equipment')}>
              <ListItemIcon><BuildIcon /></ListItemIcon>
              <ListItemText primary="Equipment / Tools" />
            </ListItemButton>
            <ListItemButton selected={page==='containers'} onClick={()=>setPage('containers')}>
              <ListItemIcon><BuildIcon /></ListItemIcon>
              <ListItemText primary="Packaging Containers" />
            </ListItemButton>
            <ListItemButton selected={page==='units'} onClick={()=>setPage('units')}>
              <ListItemIcon><ScaleIcon /></ListItemIcon>
              <ListItemText primary="Units" />
            </ListItemButton>
          </List>

          {/* Export */}
          <List subheader={<ListSubheader>Export</ListSubheader>}>
            <ListItemButton selected={page==='export'} onClick={()=>setPage('export')}>
              <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
              <ListItemText primary="Export Recipes" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {renderPage()}
      </Box>
    </Box>
  );
};

export default AdminPanel;
